import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, SearchBox, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { MARKET_DOCS, DocUpload, ExpiryBadge, TripStatus, useMarketRoles, nativeInput } from "@/components/market";

const EMPTY = { booking_id: "", vendor_id: "", vehicle_number: "", driver_name: "", driver_phone: "", dl_number: "", aadhaar_front_file_id: null, aadhaar_back_file_id: null, freight: 0, tds_percent: 0, mamul_amount: 0, remarks: "", ...Object.fromEntries(MARKET_DOCS.flatMap(([k]) => [[`${k}_file_id`, null], [`${k}_expiry`, ""]])) };

function DocsChecklist({ f, setF }) {
  return (
    <div className="rounded-xl border p-4" data-testid="market-docs-section">
      <div className="label-xs mb-3">Upload Vehicle & Driver Documents</div>
      <div className="space-y-2">
        {MARKET_DOCS.map(([k, label]) => (
          <div key={k} className="flex flex-wrap items-center gap-3 rounded-lg bg-secondary/40 p-2.5">
            <div className="w-48 text-sm font-medium">{label}</div>
            <DocUpload label="" value={f[`${k}_file_id`]} onChange={(id) => setF((x) => ({ ...x, [`${k}_file_id`]: id }))} testid={`doc-upload-${k}`} />
            <Input type="date" className="h-8 w-40" data-testid={`doc-expiry-${k}`} value={f[`${k}_expiry`] || ""} onChange={(e) => setF((x) => ({ ...x, [`${k}_expiry`]: e.target.value }))} />
            {f[`${k}_expiry`] && <ExpiryBadge date={f[`${k}_expiry`]} testid={`doc-badge-${k}`} />}
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-secondary/40 p-2.5">
          <div className="w-48 text-sm font-medium">Driver Aadhaar / ID Proof</div>
          <DocUpload label="Front" value={f.aadhaar_front_file_id} onChange={(id) => setF((x) => ({ ...x, aadhaar_front_file_id: id }))} testid="doc-upload-aadhaar-front" />
          <DocUpload label="Back" value={f.aadhaar_back_file_id} onChange={(id) => setF((x) => ({ ...x, aadhaar_back_file_id: id }))} testid="doc-upload-aadhaar-back" />
        </div>
      </div>
    </div>
  );
}

export function TripDialog({ open, initial, onClose, onSaved }) {
  const { canFin, canOps } = useMarketRoles();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial } : EMPTY);
    api.get("/market/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, [open, initial]);
  const lrs = bookings.filter((b) => !b.market_trip_id || b.id === initial?.booking_id);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickVendor = (id) => {
    const v = vendors.find((x) => x.id === id);
    setF({ ...f, vendor_id: id, tds_percent: v ? (v.tds_exempt || v.declaration_valid ? 0 : v.default_tds_percent) : f.tds_percent });
  };
  const perMt = f.hire_rate_type === "per_mt";
  const gross = perMt ? Math.round((Number(f.hire_rate) || 0) * (Number(f.hire_weight) || 0) * 100) / 100 : Number(f.freight) || 0;
  const tds = (gross * (Number(f.tds_percent) || 0)) / 100;
  const submit = async (override = false) => {
    setBusy(true);
    try {
      const body = { ...f, hire_rate_type: f.hire_rate_type || "fixed", hire_rate: perMt ? Number(f.hire_rate) || 0 : 0, hire_weight: perMt ? Number(f.hire_weight) || 0 : 0, freight: gross, tds_percent: Number(f.tds_percent) || 0, mamul_amount: Number(f.mamul_amount) || 0, override_expired: override };
      const { data } = initial ? await api.put(`/market/trips/${initial.id}`, body) : await api.post("/market/trips", body);
      setWarn(null);
      toast.success(data.compliance === "pending_approval" ? "Saved — sent for admin approval (expired documents)" : initial ? "Market trip updated" : "Market vehicle hired");
      onSaved(data);
    } catch (err) {
      if (err.response?.status === 409 && !override && String(err.response.data?.detail).startsWith("Warning")) setWarn(err.response.data.detail);
      else toast.error(errMsg(err));
    } finally { setBusy(false); }
  };
  const vendorSel = vendors.find((v) => v.id === f.vendor_id);
  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-card" data-testid="market-trip-dialog">
          <DialogHeader><DialogTitle>{initial ? "Edit Market Trip" : "Hire Market Vehicle"}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-4">
            {canOps && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Booking / LR *">
                  <select required data-testid="trip-lr-select" className={nativeInput} value={f.booking_id} onChange={set("booking_id")}>
                    <option value="">Select LR…</option>
                    {lrs.map((b) => <option key={b.id} value={b.id}>{`${b.lr_number} · ${b.pickup_location} → ${b.drop_location} · ${fmtDate(b.booking_date)}`}</option>)}
                  </select>
                </Field>
                <Field label="Vendor *">
                  <select required data-testid="trip-vendor-select" className={nativeInput} value={f.vendor_id} onChange={(e) => pickVendor(e.target.value)}>
                    <option value="">Select vendor…</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{`${v.name} · ${v.category}`}</option>)}
                  </select>
                </Field>
                <Field label="Vehicle Number *"><Input required data-testid="trip-vehicle-input" className="uppercase" value={f.vehicle_number} onChange={set("vehicle_number")} placeholder="MH12AB1234" /></Field>
                <Field label="Driver Name"><Input data-testid="trip-driver-name-input" value={f.driver_name} onChange={set("driver_name")} /></Field>
                <Field label="Driver Mobile"><Input data-testid="trip-driver-phone-input" value={f.driver_phone} onChange={set("driver_phone")} /></Field>
                <Field label="DL Number"><Input data-testid="trip-dl-number-input" value={f.dl_number} onChange={set("dl_number")} /></Field>
              </div>
            )}
            {canFin && (
              <div className="rounded-xl border p-4" data-testid="trip-freight-section">
                <div className="label-xs mb-3">Freight & Deductions</div>
                <div className="grid gap-3 sm:grid-cols-4">
                  <Field label="Hire Rate Type">
                    <select data-testid="trip-rate-type-select" className={nativeInput} value={f.hire_rate_type || "fixed"} onChange={set("hire_rate_type")}>
                      <option value="fixed">Fixed Amount</option>
                      <option value="per_mt">Per MT</option>
                    </select>
                  </Field>
                  {perMt ? <>
                    <Field label="Rate per MT (₹) *"><Input type="number" min="0" step="0.01" data-testid="trip-rate-input" value={f.hire_rate || ""} onChange={set("hire_rate")} /></Field>
                    <Field label="Weight (MT) *"><Input type="number" min="0" step="0.001" data-testid="trip-weight-input" value={f.hire_weight || ""} onChange={set("hire_weight")} /></Field>
                    <Field label="Agreed Market Freight (auto)"><div className="flex h-10 items-center rounded-md bg-secondary px-3 text-sm amount" data-testid="trip-freight-auto">{inr(gross, 2)}</div></Field>
                  </> : <Field label="Agreed Market Freight (₹)"><Input type="number" min="0" data-testid="trip-freight-input" value={f.freight} onChange={set("freight")} /></Field>}
                  <Field label="TDS %">
                    <Input type="number" step="0.01" min="0" max="20" data-testid="trip-tds-input" disabled={!!vendorSel?.tds_exempt} value={vendorSel?.tds_exempt ? 0 : f.tds_percent} onChange={set("tds_percent")} />
                  </Field>
                  <Field label="TDS Amount (auto)"><div className="flex h-10 items-center rounded-md bg-secondary px-3 text-sm amount" data-testid="trip-tds-amount">{inr(tds, 2)}</div></Field>
                  <Field label="Mamul / Commission (₹)"><Input type="number" min="0" data-testid="trip-mamul-input" value={f.mamul_amount} onChange={set("mamul_amount")} /></Field>
                </div>
                {vendorSel && <p className="mt-2 text-[11px] text-muted-foreground">{vendorSel.tds_exempt ? "Vendor TDS Exempt — TDS 0%." : vendorSel.declaration_valid ? "Valid 194C declaration on file — TDS 0% applied." : `No valid declaration — default TDS ${vendorSel.default_tds_percent}% applied.`} Advance payments can be added from the trip screen.</p>}
              </div>
            )}
            {canOps && <DocsChecklist f={f} setF={setF} />}
            <Field label="Remarks"><Input data-testid="trip-remarks-input" value={f.remarks} onChange={set("remarks")} /></Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" data-testid="trip-save-btn" disabled={busy}>{initial ? "Save Changes" : "Hire Vehicle"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!warn} onOpenChange={() => setWarn(null)}>
        <DialogContent className="max-w-md bg-card" data-testid="expired-docs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-5 w-5" />Document Expired</DialogTitle>
            <DialogDescription data-testid="expired-docs-message">{warn}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="expired-cancel-btn" onClick={() => setWarn(null)}>Go Back</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="expired-proceed-btn" disabled={busy} onClick={() => submit(true)}>Proceed with Admin Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DocsAlert({ t }) {
  const bad = (t.docs || []).filter((d) => d.state === "expired" || d.state === "expiring");
  if (!bad.length) return <span className="text-xs text-emerald-600">OK</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {bad.map((d) => <ExpiryBadge key={d.key} date={d.expiry} testid={`trip-doc-${t.id}-${d.key}`} />)}
    </div>
  );
}

export default function MarketTrips() {
  const { canFin, canOps } = useMarketRoles();
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/market/trips", { params: status ? { status } : {} }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => list.filter((t) => `${t.lr_number} ${t.vehicle_number} ${t.vendor_name} ${t.driver_name}`.toLowerCase().includes(q.toLowerCase())), [list, q]);
  const del = async (t) => {
    if (!window.confirm(`Delete market trip ${t.lr_number} (${t.vehicle_number})? LR rahega, sirf market gaadi ka trip hatega.`)) return;
    try { await api.delete(`/market/trips/${t.id}`); toast.success("Market trip deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Market Trips" subtitle="Hired market vehicles linked to your LRs">
        <SearchBox value={q} onChange={setQ} placeholder="Search LR, vehicle, vendor" testid="market-trip-search" />
        <select data-testid="market-status-filter" className="native-select h-10 w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All status</option>
          {["Active", "POD Verified", "Settled"].map((s) => <option key={s}>{s}</option>)}
        </select>
        {canOps && <Button data-testid="hire-market-vehicle-btn" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />Hire Market Vehicle</Button>}
      </PageHeader>
      <TableWrap>
        <thead><tr>{["LR", "Vehicle", "Vendor", "Route", "Documents", ...(canFin ? ["Net Payable", "Outstanding"] : []), "Status", ...(isAdmin ? [""] : [])].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className={tr} data-testid={`market-trip-row-${t.id}`}>
              <td className={td}><Link to={`/market/trips/${t.id}`} className="font-semibold text-primary hover:underline" data-testid={`market-trip-link-${t.id}`}>{t.lr_number}</Link><div className="text-xs text-muted-foreground">{fmtDate(t.booking_date)}</div></td>
              <td className={`${td} font-mono`}>{t.vehicle_number}</td>
              <td className={td}>{t.vendor_name}</td>
              <td className={td}>{t.route}</td>
              <td className={td}><DocsAlert t={t} />{t.compliance === "pending_approval" && <div className="mt-1 text-[11px] font-semibold text-rose-600">Admin approval pending</div>}</td>
              {canFin && <td className={`${td} amount`}>{inr(t.totals?.net_payable)}</td>}
              {canFin && <td className={`${td} amount font-semibold ${t.totals?.outstanding > 0 ? "text-rose-600" : ""}`}>{inr(t.totals?.outstanding)}</td>}
              <td className={td}><TripStatus status={t.status} testid={`market-trip-status-${t.id}`} /></td>
              {isAdmin && <td className={td}><Button size="sm" variant="outline" title="Delete" data-testid={`market-trip-delete-${t.id}`} onClick={() => del(t)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button></td>}
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid="market-trips-empty" text="No market trips yet" />}
      <TripDialog open={open} onClose={() => setOpen(false)} onSaved={(d) => { setOpen(false); navigate(`/market/trips/${d.id}?send=1`); }} />
    </div>
  );
}
